// TanStack Query hooks — one per endpoint.
//
// These replace the `async` server components. The mapping is close to
// mechanical:
//
//   async server component  → useQuery
//   force-dynamic           → staleTime: 0 (the default here)
//   loading.tsx             → the isPending branch
//   revalidatePath("/x")    → invalidateQueries({ queryKey: keys.x })
//   router.refresh()        → invalidateQueries
//
// The keys below mirror the old revalidatePath targets one-for-one, so porting
// each mutation in phase 7b is a lookup rather than a judgement call.

import { useQuery } from "@tanstack/react-query";
import type {
  ActiveRound,
  AlbumWithCount,
  CalendarEvent,
  CalendarMonth,
  EventOccurrence,
  FinishedRound,
  HomeSummary,
  IllustrationWithSrc,
  MessageWithSrc,
  MilestoneSummary,
  MilestoneWithPhotosSrc,
  Notification,
  NotificationList,
  PendingGame,
  OurNumbers,
  PhotoSummary,
  PhotoWithSrc,
  PlaceWithCount,
  Scoreboard,
  TimedGameId,
  TimedRoundView,
  User,
} from "@historia/shared/contracts";
import type { Album, Message, Milestone, Place } from "@historia/shared/types";
import { api } from "./client";

export const keys = {
  me: ["me"] as const,
  timeline: ["timeline"] as const,
  milestones: ["milestones"] as const,
  milestoneSummary: ["milestones", "summary"] as const,
  albums: ["albums"] as const,
  albumsWithCounts: ["albums", "counts"] as const,
  album: (id: string) => ["albums", id] as const,
  photos: (albumId?: string) => ["photos", albumId ?? "all"] as const,
  photoSummary: ["photos", "summary"] as const,
  featuredPhoto: ["photos", "featured"] as const,
  illustrations: ["illustrations"] as const,
  illustration: (id: string) => ["illustrations", id] as const,
  messages: ["messages"] as const,
  message: (id: string) => ["messages", id] as const,
  numbers: ["numbers"] as const,
  home: ["home"] as const,
  places: ["places"] as const,
  placesWithCounts: ["places", "counts"] as const,
  place: (id: string) => ["places", id] as const,
  placePhotos: (id: string) => ["places", id, "photos"] as const,
  events: ["events"] as const,
  event: (id: string) => ["events", id] as const,
  calendarMonth: (year: number, month: number) => ["events", "calendar", year, month] as const,
  upcomingEvents: (limit: number) => ["events", "calendar", "upcoming", limit] as const,
  scoreboard: ["games", "scoreboard"] as const,
  activeRound: ["games", "rounds", "active"] as const,
  finishedRounds: ["games", "rounds", "finished"] as const,
  timedActiveRound: (jogo: TimedGameId) => ["games", jogo, "rounds", "active"] as const,
  timedFinishedRounds: (jogo: TimedGameId) => ["games", jogo, "rounds", "finished"] as const,
  pendingGames: ["games", "pending"] as const,
  notifications: ["notifications"] as const,
  unreadCount: ["notifications", "unread"] as const,
  pushKey: ["push", "key"] as const,
};

// ------------------------------------------------------------
//  Session
// ------------------------------------------------------------

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<User>("/me"),
    // A 401 means "not logged in", which is an answer, not a failure to retry.
    retry: false,
  });
}

// ------------------------------------------------------------
//  Content
// ------------------------------------------------------------

export function useTimeline() {
  return useQuery({
    queryKey: keys.timeline,
    queryFn: () => api.get<MilestoneWithPhotosSrc[]>("/timeline"),
  });
}

export function useMilestones() {
  return useQuery({
    queryKey: keys.milestones,
    queryFn: () => api.get<Milestone[]>("/milestones"),
  });
}

/** id/titulo/data only — for the form selects. */
export function useMilestoneSummary() {
  return useQuery({
    queryKey: keys.milestoneSummary,
    queryFn: () => api.get<MilestoneSummary[]>("/milestones?summary=1"),
  });
}

export function useAlbums() {
  return useQuery({
    queryKey: keys.albums,
    queryFn: () => api.get<Album[]>("/albums"),
  });
}

export function useAlbumsWithCounts() {
  return useQuery({
    queryKey: keys.albumsWithCounts,
    queryFn: () => api.get<AlbumWithCount[]>("/albums?counts=1"),
  });
}

export function useAlbum(id: string | undefined) {
  return useQuery({
    queryKey: keys.album(id ?? ""),
    queryFn: () => api.get<Album>(`/albums/${id}`),
    enabled: Boolean(id),
  });
}

/** `albumId` may be a uuid, the "conto" sentinel, or undefined for everything. */
export function usePhotos(albumId?: string) {
  return useQuery({
    queryKey: keys.photos(albumId),
    queryFn: () =>
      api.get<PhotoWithSrc[]>(albumId ? `/photos?album=${encodeURIComponent(albumId)}` : "/photos"),
  });
}

export function usePhotoSummary() {
  return useQuery({
    queryKey: keys.photoSummary,
    queryFn: () => api.get<PhotoSummary[]>("/photos?summary=1"),
  });
}

export function useFeaturedPhoto() {
  return useQuery({
    queryKey: keys.featuredPhoto,
    queryFn: () => api.get<PhotoWithSrc | null>("/photos/featured"),
  });
}

export function useIllustrations() {
  return useQuery({
    queryKey: keys.illustrations,
    queryFn: () => api.get<IllustrationWithSrc[]>("/illustrations"),
  });
}

export function useMessages() {
  return useQuery({
    queryKey: keys.messages,
    queryFn: () => api.get<MessageWithSrc[]>("/messages"),
  });
}

export function useMessage(id: string) {
  return useQuery({
    queryKey: keys.message(id),
    queryFn: () => api.get<Message>(`/messages/${id}`),
    enabled: Boolean(id),
  });
}

export function useNumbers() {
  return useQuery({
    queryKey: keys.numbers,
    queryFn: () => api.get<OurNumbers>("/numbers"),
  });
}

// ------------------------------------------------------------
//  Games
// ------------------------------------------------------------

export function useScoreboard() {
  return useQuery({
    queryKey: keys.scoreboard,
    queryFn: () => api.get<Scoreboard>("/games/scoreboard"),
  });
}

export function useActiveRound() {
  return useQuery({
    queryKey: keys.activeRound,
    queryFn: () => api.get<ActiveRound | null>("/games/rounds/active"),
  });
}

export function useFinishedRounds() {
  return useQuery({
    queryKey: keys.finishedRounds,
    queryFn: () => api.get<FinishedRound[]>("/games/rounds/finished"),
  });
}

// ------------------------------------------------------------
//  Home screen (IDEA-029)
// ------------------------------------------------------------

/** Every widget's data in one request — see the contract for why it's one. */
export function useHome() {
  return useQuery({
    queryKey: keys.home,
    queryFn: () => api.get<HomeSummary>("/home"),
  });
}

// ------------------------------------------------------------
//  Places — the map (IDEA-014)
// ------------------------------------------------------------

export function usePlaces() {
  return useQuery({
    queryKey: keys.places,
    queryFn: () => api.get<Place[]>("/places"),
  });
}

export function usePlacesWithCounts() {
  return useQuery({
    queryKey: keys.placesWithCounts,
    queryFn: () => api.get<PlaceWithCount[]>("/places?counts=1"),
  });
}

/** The photos taken at one place — loaded only when a marker is opened. */
export function usePlacePhotos(id: string | null) {
  return useQuery({
    queryKey: keys.placePhotos(id ?? ""),
    queryFn: () => api.get<PhotoWithSrc[]>(`/places/${id}/photos`),
    enabled: Boolean(id),
  });
}

// ------------------------------------------------------------
//  Events — the shared calendar (IDEA-030)
// ------------------------------------------------------------

export function useEvents() {
  return useQuery({
    queryKey: keys.events,
    queryFn: () => api.get<CalendarEvent[]>("/events"),
  });
}

/** One month's occurrences, with annual events already expanded server-side. */
export function useCalendarMonth(year: number, month: number) {
  return useQuery({
    queryKey: keys.calendarMonth(year, month),
    queryFn: () => api.get<CalendarMonth>(`/events/calendar?year=${year}&month=${month}`),
    // Moving between months shouldn't blank the grid — keep the previous month
    // on screen until the next one arrives.
    placeholderData: (previous) => previous,
  });
}

export function useUpcomingEvents(limit = 5) {
  return useQuery({
    queryKey: keys.upcomingEvents(limit),
    queryFn: () => api.get<EventOccurrence[]>(`/events/calendar?upcoming=${limit}`),
  });
}

// ------------------------------------------------------------
//  Timed games — Memória and Shikaku (IDEA-031, IDEA-032)
// ------------------------------------------------------------

export function useTimedActiveRound(jogo: TimedGameId) {
  return useQuery({
    queryKey: keys.timedActiveRound(jogo),
    queryFn: () => api.get<TimedRoundView | null>(`/games/${jogo}/rounds/active`),
  });
}

export function useTimedFinishedRounds(jogo: TimedGameId) {
  return useQuery({
    queryKey: keys.timedFinishedRounds(jogo),
    queryFn: () => api.get<TimedRoundView[]>(`/games/${jogo}/rounds/finished`),
  });
}

// ------------------------------------------------------------
//  Notifications (IDEA-044)
// ------------------------------------------------------------

/**
 * Every round waiting for this person, across all ten games.
 *
 * Note the key sits under `["games", ...]` — so the existing
 * `AFTER_TIMED_GAME_CHANGE`, which invalidates `["games"]` wholesale, already
 * clears this the moment a board is played. That is the point of putting it
 * there: a badge that outlives the round it describes is worse than no badge.
 */
export function usePendingGames() {
  return useQuery({
    queryKey: keys.pendingGames,
    queryFn: () => api.get<PendingGame[]>("/games/pending"),
  });
}

/** The notifications page: the list and the unread count together. */
export function useNotifications() {
  return useQuery({
    queryKey: keys.notifications,
    queryFn: () => api.get<NotificationList>("/notifications"),
  });
}

/**
 * Just the count, for the badge in the navigation.
 *
 * Separate from the list because it is asked for on every page: the badge wants
 * a number, not sixty rows of text it will never render.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: keys.unreadCount,
    queryFn: () => api.get<{ naoLidas: number }>("/notifications/unread"),
  });
}

/**
 * The VAPID public key, and whether this account already has a device signed up.
 *
 * `chavePublica: null` means the server has no keys configured — push is off
 * server-side, and the settings page says so rather than offering a switch that
 * cannot work.
 */
export function usePushKey() {
  return useQuery({
    queryKey: keys.pushKey,
    queryFn: () => api.get<{ chavePublica: string | null; subscrito: boolean }>("/push/key"),
    // The key never changes while the server is up, and neither does support.
    staleTime: 5 * 60 * 1000,
  });
}

export type { Notification };
